import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, MapPin, Home as HomeIcon, Wrench, TrendingUp, CheckCircle2, Phone, MessageCircle, Sparkles, Eye } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useT, useTRaw, useImg, useContent } from "@/context/ContentContext";
import { renderBold } from "@/lib/markdown";
import { api, fileUrl } from "@/lib/api";
import PageHead from "@/components/PageHead";
import BeforeAfterGallery from "@/components/BeforeAfterGallery";
import LandingBlogSection from "@/components/LandingBlogSection";
import NewsletterPromoBar from "@/components/NewsletterPromoBar";

// Fallback stock pairs — replaced by CMS data when present
const FALLBACK = [
    {
        title: "Cozinha renovada",
        subtitle: "Apartamento T2",
        before: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        after: "https://images.unsplash.com/photo-1714860534425-7ce04e013dec?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    },
    {
        title: "Casa de banho premium",
        subtitle: "Moradia",
        before: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        after: "https://images.unsplash.com/photo-1763485955998-a9284d042d50?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    },
    {
        title: "Sala e acabamentos",
        subtitle: "Apartamento T3",
        before: "https://images.unsplash.com/photo-1554995207-c18c203602cb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        after: "https://images.unsplash.com/photo-1738168279272-c08d6dd22002?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    },
];

export default function LocalLanding({ city, hero, canonicalSlug }) {
    const { settings, whatsappLink } = useSettings();
    const t = useT();
    const tRaw = useTRaw();
    const img = useImg();
    const { content } = useContent();
    const [items, setItems] = useState([]);

    // Content keys are namespaced by the canonical slug so each landing is independent.
    const K = (suffix) => `${canonicalSlug}.${suffix}`;
    const heroImg = img(K("image"), hero);
    const heroSubtitle = t(K("subtitle"), `Obras completas, gestão de obra e valorização de imóveis em ${city} e arredores. Um interlocutor único, orçamentos claros e atualizações da obra com fotografias.`);
    const whyIntro = t(K("why_intro"), `Trabalhamos regularmente em ${city} — conhecemos a realidade dos imóveis, dos condomínios e dos prazos locais. Tratamos das especialidades, coordenamos os trabalhos e garantimos que fica com um único interlocutor do início ao fim.`);
    const finalCtaText = t(K("cta_text"), "Conte-nos o que pretende fazer. Analisamos consigo os próximos passos, sem compromisso.");

    useEffect(() => {
        api.get("/before-after").then((r) => {
            if (r.data?.length) {
                setItems(r.data.map((it) => ({
                    title: it.title,
                    subtitle: it.subtitle,
                    before: fileUrl(it.before_id),
                    after: fileUrl(it.after_id),
                })));
            }
        }).catch(() => {});
    }, []);

    const gallery = items.length ? items : FALLBACK;

    const title = `Remodelações em ${city} — O Homem da Obra`;
    const description = `Serviço de remodelação, gestão de obra e valorização de imóveis em ${city}. Orçamentos claros, um interlocutor e obras sem dores de cabeça. Peça o seu orçamento.`;
    const canonical = `https://ohomemdaobra.pt/${canonicalSlug}`;

    return (
        <>
            <PageHead title={title} description={description} canonical={canonical} />

            {/* HERO */}
            <section className="relative pt-24 pb-8 sm:pb-14">
                <div className="max-w-[1400px] mx-auto px-3 sm:px-4">
                    <div className="relative rounded-[2rem] overflow-hidden min-h-[75vh] bg-[hsl(var(--forest))]">
                        <img src={heroImg} alt={`Remodelações em ${city}`} className="absolute inset-0 w-full h-full object-cover opacity-55" />
                        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--forest))]/50 via-[hsl(var(--forest))]/40 to-[hsl(var(--forest))]/85" />
                        <div className="relative h-full min-h-[75vh] flex flex-col justify-between p-6 sm:p-10 lg:p-16">
                            <div />
                            <div>
                                <div className="eyebrow-light mb-6"><MapPin className="w-3.5 h-3.5" /> {city}</div>
                                <h1 className="font-display text-white text-4xl sm:text-6xl lg:text-7xl font-bold leading-[0.98] tracking-tight max-w-4xl" data-testid={`landing-h1-${canonicalSlug}`}>
                                    Remodelações em <span className="font-serif-italic text-[hsl(var(--stone))]">{city}</span><span className="text-[hsl(var(--copper))]">.</span>
                                </h1>
                                <p className="text-white/85 text-lg leading-relaxed mt-8 max-w-2xl">
                                    {heroSubtitle}
                                </p>
                                <div className="flex flex-wrap gap-3 mt-10">
                                    <Link to="/orcamento" className="btn-copper" data-testid={`landing-cta-${canonicalSlug}`}>
                                        Pedir orçamento em {city}
                                        <span className="arrow-circle"><ArrowUpRight className="w-3.5 h-3.5" /></span>
                                    </Link>
                                    <a href={`tel:${settings.phone.replace(/\s/g, "")}`} className="bg-white/10 backdrop-blur border border-white/30 text-white px-6 py-3.5 rounded-full font-medium inline-flex items-center gap-2 hover:bg-white/20 transition">
                                        <Phone className="w-4 h-4" /> Ligar agora
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <NewsletterPromoBar source={`landing-${canonicalSlug}`} />

            {/* PORQUÊ EM CIDADE */}
            <section className="py-16 sm:py-24 bg-white">
                <div className="max-w-6xl mx-auto px-6 sm:px-10 grid lg:grid-cols-2 gap-12 items-start">
                    <div>
                        <div className="eyebrow mb-6"><CheckCircle2 className="w-3.5 h-3.5" /> Porquê O Homem da Obra em {city}</div>
                        <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
                            Uma obra bem feita, <span className="font-serif-italic text-[hsl(var(--copper))]">à porta de casa</span>.
                        </h2>
                        <p className="text-[hsl(var(--graphite))]/75 text-lg mt-6 leading-relaxed">
                            {whyIntro}
                        </p>
                    </div>
                    <ul className="space-y-3">
                        {[
                            { icon: HomeIcon, kt: "landing.point1.title", kd: "landing.point1.desc", dt: "Remodelações completas", dd: "Apartamentos, moradias e imóveis para investimento em {city}." },
                            { icon: Wrench, kt: "landing.point2.title", kd: "landing.point2.desc", dt: "Gestão de obra completa", dd: "Coordenamos empreiteiros, fiscalizamos e mantemos o cliente com um único interlocutor." },
                            { icon: TrendingUp, kt: "landing.point3.title", kd: "landing.point3.desc", dt: "Valorização para arrendamento/venda", dd: "Preparamos o imóvel para maximizar o retorno." },
                            { icon: CheckCircle2, kt: "landing.point4.title", kd: "landing.point4.desc", dt: "Orçamento discriminado", dd: "Sem letra miúda. Se algo mudar durante a obra, explicamos antes." },
                            { icon: Eye, kt: "landing.point5.title", kd: "landing.point5.desc", dt: "Obra sempre acompanhada", dd: "Saiba o que foi feito e o que acontece a seguir." },
                        ]
                        // Hide any point whose title was explicitly cleared in the CMS.
                        .filter((f) => content[f.kt] !== "")
                        .map((f, i) => {
                            // For each field: explicit "" means "user cleared" → render empty; undefined means "never saved" → use default.
                            const titleSrc = content[f.kt] !== undefined ? content[f.kt] : f.dt;
                            const descSrc = content[f.kd] !== undefined ? content[f.kd] : f.dd;
                            const titleRaw = String(titleSrc).replace(/\{city\}/g, city);
                            const descRaw = String(descSrc).replace(/\{city\}/g, city);
                            return (
                                <li key={i} className="card-stone flex items-start gap-4">
                                    <div className="w-11 h-11 rounded-full bg-[hsl(var(--forest))] text-white flex items-center justify-center flex-shrink-0">
                                        <f.icon className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <div className="font-display font-bold mb-1">{renderBold(titleRaw)}</div>
                                        {descRaw && <p className="text-sm text-[hsl(var(--graphite))]/70">{renderBold(descRaw)}</p>}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </section>

            {/* ANTES & DEPOIS */}
            <section className="py-14 sm:py-20">
                <div className="max-w-6xl mx-auto px-6 sm:px-10">
                    <div className="eyebrow mb-6"><Sparkles className="w-3.5 h-3.5" /> Transformações</div>
                    <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl mb-4">
                        Antes & <span className="font-serif-italic text-[hsl(var(--copper))]">depois</span>.
                    </h2>
                    <p className="text-[hsl(var(--graphite))]/70 text-lg max-w-2xl mb-10">
                        Arraste o cursor ou use as setas para percorrer alguns dos trabalhos realizados.
                    </p>
                    <BeforeAfterGallery items={gallery} />
                </div>
            </section>

            <LandingBlogSection city={city} />

            {/* CTA */}
            <section className="pb-20">
                <div className="max-w-[1400px] mx-auto px-3 sm:px-4">
                    <div className="bg-[hsl(var(--forest))] rounded-[2rem] p-10 sm:p-16 text-center text-white">
                        <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl mx-auto">
                            Está a pensar remodelar em <span className="font-serif-italic text-[hsl(var(--copper))]">{city}</span>?
                        </h2>
                        <p className="text-white/80 text-lg mt-6 max-w-2xl mx-auto">
                            {finalCtaText}
                        </p>
                        <div className="flex flex-wrap gap-3 justify-center mt-10">
                            <Link to="/orcamento" className="btn-copper">
                                Fale-nos da sua obra
                                <span className="arrow-circle"><ArrowUpRight className="w-3.5 h-3.5" /></span>
                            </Link>
                            <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="bg-white/10 backdrop-blur border border-white/30 text-white px-6 py-3.5 rounded-full font-medium inline-flex items-center gap-2 hover:bg-white/20 transition">
                                <MessageCircle className="w-4 h-4" /> WhatsApp
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
